import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { applyBrandVars, isLightHex as isLight } from "@/lib/branding";
import { AddToCalendar } from "@/components/AddToCalendar";
import { StepData, StepSituations, visibleSituations } from "@/components/OwnerForm";
import { BuyerStepData } from "@/components/BuyerForm";
import { KycStep, StepBar } from "@/components/PortalKyc";
import { PORTAL_MIDDLE } from "@/lib/legalTexts";
import { fmtPhone } from "@/lib/format";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Loader2, CheckCircle2, ShieldCheck, Building2, ChevronLeft, ChevronRight, FileCheck } from "lucide-react";
import { Unrecognized } from "@/components/PortalBulk";
import { CertidaoQuestion } from "@/components/CertidaoQuestion";
import { usePortalEnglish } from "@/lib/portalI18n";
import { CC_KEYS, DARK, DocumentStatusCard as DocCard, HelpSheet } from "@/components/portal/DocCards";
import { PortalQuestions } from "@/components/portal/PortalQuestions";
import { WhatNeedsYouNow, NowRow, splitDocs } from "@/components/portal/WhatNeedsYouNow";
import { DocScreen } from "@/components/portal/DocScreen";
import { sendTitle, sendWhy } from "@/components/portal/copy";
import { PoweredBy } from "@/components/PoweredBy";
import { DocSections } from "@/components/portal/DocSections";
import { ProcessProgress, PurchaseInfo, deal } from "@/components/portal/ProcessProgress";

export { ConsultantRow, Progress } from "@/components/portal/ProcessProgress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Os 5 passos do portal (só os que se aplicam): cartão, dados, situações, identificação e documentos.
function portalSteps(data, form, bform, kyc) {
  const seller = data.role === "vendedor";
  const cc = data.docs.find((d) => CC_KEYS.includes(d.key));
  const out = [];
  if (cc) out.push({ key: "cc", label: "Cartão", done: !!["a_verificar", "ok"].includes(cc.state) || cc.later });
  if (seller && form.required) {
    out.push({ key: "dados", label: "Os seus dados", done: !!form.step1_done });
    if (visibleSituations(form).length) out.push({ key: "sit", label: "Situações", done: !!form.done });
  }
  if (!seller && bform.required) out.push({ key: "dados", label: "Os seus dados", done: !!bform.done });
  if (kyc.enabled) out.push({ key: "id", label: "Identificação", done: !!kyc.done });
  out.push({ key: "docs", label: "Documentos", done: data.pending.length === 0 });
  return out;
}
const NEUTRAL = "#12332B";

function Gate({ info, onVerified, token }) {
  const [last4, setLast4] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [ack, setAck] = useState(!!info.privacy_ack);
  const [mkt, setMkt] = useState(false);
  const mode = info.auth_mode || "phone";
  const len = mode === "email" ? 6 : 4;
  const sendCode = async () => {
    setErr("");
    try { await axios.post(`${API}/portal/${token}/send-code`); setSent(true); }
    catch (ex) { const det = ex.response?.data?.detail; setErr(typeof det === "string" ? det : "Não foi possível enviar o código."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/portal/${token}/verify`, { last4, privacy_ack: ack, marketing: mkt });
      onVerified(data.session);
    } catch (ex) {
      const det = ex.response?.data?.detail;
      setErr(typeof det === "string" ? det : "Não foi possível verificar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="portal-gate">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <ShieldCheck size={18} className="brand-text" />
        Para sua segurança, confirme a sua identidade.
      </div>
      <div>
        <label className="text-sm font-medium brand-text">
          {mode === "phone" ? "Últimos 4 dígitos do seu telemóvel" : mode === "email" ? "Código de 6 dígitos enviado para o seu email" : "Código de acesso que a agência lhe indicou"}
        </label>
        {mode === "email" && (
          <button type="button" onClick={sendCode} data-testid="portal-send-code" className="block mt-2 text-sm underline accent-text">
            {sent ? "Código enviado. Reenviar" : "Enviar código por email"}
          </button>
        )}
        <input
          value={last4}
          onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, len))}
          inputMode="numeric" placeholder={"•".repeat(len)} maxLength={len} autoFocus
          data-testid="portal-last4-input"
          className="mt-2 w-full text-center tracking-[0.5em] text-2xl font-mono rounded-xl border-2 border-slate-200 brand-focus outline-none py-3"
        />
      </div>
      {err && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2" data-testid="portal-gate-error">{err}</div>}
      {!info.privacy_ack && info.privacy && (
        <div className="space-y-3">
          <label className="flex items-start gap-3 text-[15px] text-slate-600 leading-snug cursor-pointer" data-testid="portal-privacy-ack">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0 accent-slate-800" data-testid="portal-privacy-ack-input" />
            <span>Li e compreendi <PrivacyNotice p={info.privacy} label="como os meus dados são tratados" className="inline underline underline-offset-2 accent-text font-medium text-left" />. <span className="text-slate-400">(obrigatório)</span></span>
          </label>
          <label className="flex items-start gap-3 text-[15px] text-slate-600 leading-snug cursor-pointer" data-testid="portal-marketing">
            <input type="checkbox" checked={mkt} onChange={(e) => setMkt(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0 accent-slate-800" data-testid="portal-marketing-input" />
            <span>Aceito ser contactado pela {info.privacy.commercial || info.privacy.name} sobre outros imóveis e serviços. <span className="text-slate-400">(opcional)</span></span>
          </label>
        </div>
      )}
      <button type="submit" disabled={last4.length !== len || busy || !ack} data-testid="portal-verify-btn"
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold transition-colors disabled:opacity-40"
        style={DARK}>
        {busy ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />} Entrar
      </button>
    </form>
  );
}

function PrivacyNotice({ p, label = "Aviso de privacidade", className = "text-[11px] text-slate-500 underline underline-offset-2 hover:text-slate-700" }) {
  const [open, setOpen] = useState(false);
  const ids = [p.name !== p.commercial && p.name, p.nipc && `NIPC ${p.nipc}`, p.ami && `AMI ${p.ami}`, p.address && `sede: ${p.address}`].filter(Boolean).join(", ");
  return (
    <>
      <button type="button" onClick={(e) => { e.preventDefault(); setOpen(true); }} className={className} data-testid={label === "Aviso de privacidade" ? "portal-privacy-link" : "portal-privacy-ack-link"}>{label}</button>
      <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
        <DrawerContent className="max-h-[88vh] bg-white" data-testid="portal-privacy-sheet">
          <div className="overflow-y-auto px-5 pt-3 pb-8 max-w-xl mx-auto w-full text-sm text-slate-600 leading-relaxed">
            <DrawerTitle className="font-heading text-xl brand-text">Os seus dados e documentos</DrawerTitle>
            <DrawerDescription className="sr-only">Aviso de privacidade da agência</DrawerDescription>
            <p className="mt-4" data-testid="portal-privacy-controller">
              Os documentos e dados que envia nesta página são recolhidos por <b className="brand-text">{p.commercial || p.name}</b>{ids && ` (${ids})`}, responsável pelo seu tratamento, para preparar e acompanhar o seu processo de {p.deal || "venda"} até à escritura e para cumprir obrigações legais, incluindo a prevenção do branqueamento de capitais.
            </p>
            {PORTAL_MIDDLE.map((t, i) => <p key={t.slice(0, 20)} className="mt-4" data-testid={i === 0 ? "portal-privacy-processor" : undefined}>{t}</p>)}
            <p className="mt-4">
              Pode pedir acesso, retificação ou apagamento dos seus dados, ou opor-se ao tratamento, {p.email
                ? <>através de <a href={`mailto:${p.email}`} className="font-semibold underline accent-text" data-testid="portal-privacy-email">{p.email}</a></>
                : "junto da agência ou do seu consultor"}, e reclamar junto da CNPD (www.cnpd.pt).
            </p>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function Portal({ token, session, color, accent: accentHex, onAddress, onPurchase }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [bform, setBform] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [step, setStep] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [screen, setScreen] = useState(null);
  const [moreNeeds, setMoreNeeds] = useState(false);

  const load = () => {
    const h = { headers: { "X-Portal-Session": session } };
    axios.get(`${API}/portal/${token}/owner-form`, h).then(({ data }) => setForm(data)).catch(() => setForm({ required: false }));
    axios.get(`${API}/portal/${token}/buyer-form`, h).then(({ data }) => setBform(data)).catch(() => setBform({ required: false }));
    axios.get(`${API}/portal/${token}/kyc`, h).then(({ data }) => setKyc(data)).catch(() => setKyc({ enabled: false }));
    return axios.get(`${API}/portal/${token}/data`, h)
      .then(({ data }) => { setData(data); onAddress?.(data.property_address); onPurchase?.(data.role === "comprador" && data.cpcv_signed && data.pending.length === 0); })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const onToggleHelp = (key, open) => setOpenKey(open ? key : null);
  const handOver = (key) => axios.post(`${API}/portal/${token}/documents/${key}/consultant`, {}, { headers: { "X-Portal-Session": session } })
    .then(() => { setOpenKey(null); load(); }).catch(() => {});

  if (!data || !form || !bform || !kyc) return <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;
  const steps = portalSteps(data, form, bform, kyc);
  const cur = step || (steps.find((s) => !s.done) || steps[steps.length - 1]).key;
  const next = () => { setStep(null); window.scrollTo({ top: 0, behavior: "smooth" }); load(); };
  const ccDoc = data.docs.find((d) => CC_KEYS.includes(d.key));
  const pick = (k) => { setStep(k); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const idx = steps.findIndex((s) => s.key === cur);
  const back = idx > 0 && (
    <button type="button" onClick={() => pick(steps[idx - 1].key)} className="inline-flex items-center gap-1 -ml-1 text-sm font-semibold text-slate-600 hover:text-slate-900 py-1" data-testid="portal-back">
      <ChevronLeft size={18} /> Voltar
    </button>
  );
  // Só na agência de demonstração: passar as folhas sem as preencher.
  const fwd = data.demo && idx < steps.length - 1 && (
    <button type="button" onClick={() => pick(steps[idx + 1].key)} className="ml-auto inline-flex items-center gap-1 h-9 px-4 rounded-full bg-ec-green text-white text-sm font-semibold hover:opacity-90 transition-opacity" data-testid="portal-demo-next">
      Avançar <ChevronRight size={16} />
    </button>
  );
  const bar = steps.length > 1 && <div className="space-y-3"><StepBar steps={steps} current={cur} onPick={pick} />{(back || fwd) && <div className="flex items-center">{back}{fwd}</div>}</div>;
  const docWhy = data.stage === "angariacao" ? `Precisamos dele para ${deal(data.negocio).goal}.` : `Precisamos dele para preparar a escritura ${deal(data.negocio).ctx}.`;
  const docCtx = { arrendamento: "o arrendamento", trespasse: "o trespasse" }[data.negocio] || "a compra e venda";
  const card = (d, compact = false, bare = false, onDone = load) => <DocCard key={d.key} token={token} session={session} doc={d} consultant={data.consultant} isOpen={openKey === d.key} onToggleHelp={onToggleHelp} onUploaded={onDone} compact={compact} why={docWhy} ctx={docCtx} bare={bare} />;
  if (cur !== "docs") {
    return (
      <div className="space-y-6" data-testid="portal-flow">
        {bar}
        {cur === "cc" && ccDoc && (
          <div className="space-y-4" data-testid="portal-step-cc-body">
            <div><h2 className="font-heading text-xl text-ec-green">{ccDoc.key === "id_representante_vendedor" ? "Cartão de cidadão de quem assina" : "Cartão de cidadão"}</h2></div>
            {card(ccDoc)}
            {(ccDoc.state !== "falta" && ccDoc.state !== "pedido" || ccDoc.later) && <button onClick={next} className="w-full py-3.5 rounded-xl text-white font-semibold bg-ec-green" data-testid="portal-cc-next">Continuar</button>}
            <HelpSheet doc={openKey === ccDoc.key ? ccDoc : null} consultant={data.consultant} onClose={() => setOpenKey(null)} onHandOver={handOver} />
          </div>
        )}
        {cur === "dados" && (data.role === "vendedor" ? <StepData token={token} session={session} form={form} onNext={async () => {
          if (!visibleSituations(form).length) await axios.post(`${API}/portal/${token}/owner-special`, { special: {} }, { headers: { "X-Portal-Session": session } });
          next();
        }} />
          : <BuyerStepData token={token} session={session} form={bform} onDone={next} />)}
        {cur === "sit" && <StepSituations token={token} session={session} form={form} onBack={() => setStep("dados")} onDone={next}
          confirmLabel={kyc.enabled && !kyc.done ? "Confirmar e continuar" : "Confirmar e ver documentos"} />}
        {cur === "id" && <KycStep token={token} session={session} kyc={kyc} onDone={next} onReload={load} />}
      </div>
    );
  }

  const purchase = data.role === "comprador" && data.cpcv_signed && !data.deed_done_on;
  const stillMissing = purchase ? data.docs.filter((d) => ["falta", "pedido", "problema"].includes(d.state)) : [];
  const deedLine = [data.deed_date, data.deed_time && `às ${data.deed_time}`, data.deed_place && `em ${data.deed_place}`].filter(Boolean).join(", ");
  const { needs, review, done } = splitDocs(data.docs.filter((d) => d.key !== ccDoc?.key || d.state === "problema"));
  const cert = data.certidao;
  const certOpen = cert && !cert.code && !cert.pdf && cert.choice !== "nao_tenho";
  const certNode = cert && <CertidaoQuestion token={token} session={session} cert={cert} onChange={() => { setScreen(null); load(); }} />;
  const asks = (data.questions || []).length > 0 || certOpen;
  const unrec = (data.unrecognized || []).length ? <Unrecognized token={token} session={session} files={data.unrecognized} docs={data.docs} onDone={load} /> : null;
  const open = (k) => { setScreen(k); window.scrollTo({ top: 0 }); };
  const close = () => { setScreen(null); window.scrollTo({ top: 0 }); load(); };
  const sd = screen && screen !== "cert" && data.docs.find((d) => d.key === screen);
  if (sd) {
    return (
      <DocScreen title={sd.client_name} heading={sendTitle(sd)} why={sd.todo ? null : sendWhy(sd, docCtx, docWhy)} onBack={() => setScreen(null)}>
        {card(sd, false, true, close)}
        <HelpSheet doc={openKey === sd.key ? sd : null} consultant={data.consultant} onClose={() => setOpenKey(null)} onHandOver={(k) => { handOver(k); setScreen(null); }} />
      </DocScreen>
    );
  }
  if (screen === "cert" && cert) {
    return <DocScreen title="Certidão permanente" onBack={() => setScreen(null)} testid="portal-cert-screen">{certNode}</DocScreen>;
  }
  return (
    <div className="space-y-6" data-testid="portal-content">
      {bar}
      <ProcessProgress data={data} bform={bform} color={color} accent={accentHex} purchase={purchase} stillMissing={stillMissing} deedLine={deedLine} />

      {stillMissing.length > 0 && (
        <div data-testid="portal-purchase-docs">
          <h2 className="text-base font-semibold brand-text">Ainda precisamos de</h2>
          <div className="mt-3 space-y-3">{stillMissing.map((d) => card(d))}</div>
          <HelpSheet doc={stillMissing.find((d) => d.key === openKey)} consultant={data.consultant} onClose={() => setOpenKey(null)} onHandOver={handOver} />
        </div>
      )}
      {stillMissing.length > 0 && (
        <div className="rounded-[18px] p-4 sm:p-5 text-white" style={{ backgroundColor: color, color: "var(--on-dark)" }}><PurchaseInfo data={data} deedLine={deedLine} /></div>
      )}

      {!purchase && <>
        <WhatNeedsYouNow needs={needs} top={unrec} ctx={docCtx} why={docWhy} onOpen={open} more={moreNeeds} setMore={setMoreNeeds} />
        {!needs.length && !unrec && !asks && (
          <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-xl px-4 py-3" data-testid="portal-all-done">
            <CheckCircle2 size={18} /> Não falta nada da sua parte. Muito obrigado!
          </div>
        )}
        <DocSections review={review} done={done} card={card} doneExtra={cert && !certOpen ? certNode : null} />
        {asks && (
          <div className="space-y-4" data-testid="portal-asks">
            <PortalQuestions token={token} session={session} items={data.questions || []} onDone={load} />
            {certOpen && <NowRow icon={FileCheck} title="Tem a certidão permanente?" why="Envie o código ou o PDF. Se não tiver, a agência trata." onClick={() => open("cert")} testid="portal-cert-row" tone="bg-rose-50/40" />}
          </div>
        )}
        <HelpSheet doc={data.docs.find((d) => d.key === openKey)} consultant={data.consultant} onClose={() => setOpenKey(null)} onHandOver={handOver} />
      </>}

      {data.calendar && (
        <div className="bg-white rounded-[18px] p-4 border border-slate-100">
          <AddToCalendar cal={data.calendar} icsHref={`${API}/portal/${token}/calendar.ics`} testid="portal-cal"
            onAdded={(reload = true) => axios.post(`${API}/portal/${token}/calendar/added`).then(() => reload && load())} />
        </div>
      )}
    </div>
  );
}

// Processo concluído: só a mensagem final e o contacto do consultor (sem PIN, documentos nem dados).
function ClosedCard({ c }) {
  const k = c.consultant;
  return (
    <div className="text-center py-4" data-testid="portal-closed">
      <p className="font-heading text-xl brand-text" data-testid="portal-closed-text">{c.text}</p>
      {k && (
        <div className="mt-4 text-sm text-slate-600 space-y-1" data-testid="portal-closed-consultant">
          <div className="font-semibold text-slate-800">{k.name}</div>
          {k.phone && <a href={`tel:${k.phone.replace(/\s/g, "")}`} className="block underline underline-offset-2">{fmtPhone(k.phone)}</a>}
          {k.email && <a href={`mailto:${k.email}`} className="block underline underline-offset-2 break-all">{k.email}</a>}
        </div>
      )}
    </div>
  );
}

// O cliente muda PT | EN; a página recarrega mantendo a sessão neste separador.
function PortalLangSwitch({ token, lang, session }) {
  const [busy, setBusy] = useState(false);
  const pick = async (l) => {
    if (l === lang || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${token}/lang`, { lang: l });
      if (session) sessionStorage.setItem(`ec_portal_s_${token}`, JSON.stringify(session));
      window.location.reload();
    } catch { setBusy(false); }
  };
  return (
    <div className="ml-auto shrink-0 inline-flex rounded-full border border-slate-200 p-0.5 text-xs font-semibold" data-no-tr title="Português / English" data-testid="portal-lang-switch">
      {["pt", "en"].map((l) => (
        <button key={l} type="button" onClick={() => pick(l)} disabled={busy} data-testid={`portal-lang-switch-${l}`} aria-pressed={lang === l}
          className={`px-2.5 py-1 rounded-full transition-colors ${lang === l ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{l.toUpperCase()}</button>
      ))}
    </div>
  );
}

export default function ClientPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const ready = usePortalEnglish(token, info?.lang === "en");
  const [notFound, setNotFound] = useState(false);
  const [session, setSession] = useState(() => {
    const k = `ec_portal_s_${token}`;
    const v = sessionStorage.getItem(k);
    sessionStorage.removeItem(k);
    try { return v ? JSON.parse(v) : null; } catch { return null; }
  });
  const [addr, setAddr] = useState(null);
  const [purchaseView, setPurchaseView] = useState(false);

  useEffect(() => {
    axios.get(`${API}/portal/${token}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setNotFound(true));
  }, [token]);

  if (notFound)
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-5">
        <div className="text-center text-slate-500" data-testid="portal-not-found">
          <Building2 size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 font-heading text-lg text-slate-700">Ligação inválida</p>
          <p className="text-sm mt-1">Esta ligação já não está disponível. Contacte a sua agência.</p>
        </div>
      </div>
    );

  if (!info) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="animate-spin text-slate-400" /></div>;
  applyBrandVars(info.button_color || info.color);
  const dark = info.dark_color || NEUTRAL;
  const accent = info.accent_color || dark;
  const vars = { "--brand": isLight(dark) ? "#1f2937" : dark, "--dark": dark, "--on-dark": isLight(dark) ? "#1f2937" : "#ffffff", "--accent": accent, "--btn": info.button_color || dark };

  return (
    <>
    {!ready && <div className="fixed inset-0 z-50 grid place-items-center bg-slate-50" data-no-tr data-testid="portal-translating"><Loader2 className="animate-spin text-slate-400" /></div>}
    <div className={`min-h-screen bg-slate-50 ${isLight(dark) ? "on-light" : ""}`} style={{ ...vars, visibility: ready ? "visible" : "hidden" }} data-testid="portal-root" data-lang={info.lang || "pt"}>
      {info.test_env && <div className="bg-slate-800 text-white text-xs text-center py-1 tracking-wide" data-testid="portal-test-banner">Versão de testes</div>}
      <div className="bg-white border-b-2" style={{ borderColor: accent }} data-testid="portal-header">
        <div className="mx-auto max-w-xl px-5 py-3 flex items-center gap-3">
          {info.logo
            ? <img src={info.logo} alt={info.agency_name} className="h-12 sm:h-14 max-w-[170px] object-contain" data-testid="portal-logo" />
            : <div className="font-heading text-base sm:text-lg brand-text truncate">{info.agency_name}</div>}
          <span className="w-px h-8 bg-slate-300 shrink-0" aria-hidden="true" data-testid="portal-header-divider" />
          <div className="min-w-0">
            <div className="font-heading text-[20px] leading-tight brand-text truncate" data-testid="portal-name">{info.title}</div>
            {session && addr && <div className="text-sm text-slate-500 truncate mt-0.5" data-testid="portal-address">{addr}</div>}
          </div>
          {!info.closed && <PortalLangSwitch token={token} lang={info.lang || "pt"} session={session} />}
        </div>
      </div>
      <div className="mx-auto max-w-xl px-3 sm:px-5 py-6">
        <div className={session ? "sm:bg-white sm:rounded-[18px] sm:p-6 sm:shadow-[0_8px_30px_rgb(0,0,0,0.05)] sm:border sm:border-slate-100" : "bg-white rounded-[18px] p-4 sm:p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-slate-100"} data-testid="portal-outer">
          {info.closed ? <ClosedCard c={info.closed} /> : !session ? (
            <>
              <h1 className="font-heading text-2xl brand-text">
                Olá{info.party_first_name ? `, ${info.party_first_name}` : ""}.
              </h1>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                Esta é a sua página segura para {info.stage === "angariacao" ? "confirmar os seus dados e enviar os documentos do imóvel" : `enviar os documentos ${deal(info.negocio).of}`}.
              </p>
              <Gate info={info} token={token} onVerified={setSession} />
            </>
          ) : (
            <Portal token={token} session={session} color="var(--dark)" accent={info.accent_color || info.dark_color} onAddress={setAddr} onPurchase={setPurchaseView} />
          )}
        </div>

        {session && info.email_address && !purchaseView && (
          <p className="text-sm text-slate-600 text-center mt-6" data-testid="portal-email-address">
            Prefere email? Envie os documentos para <a href={`mailto:${info.email_address}`} className="font-semibold accent-text underline underline-offset-2 break-all">{info.email_address}</a>
          </p>
        )}
        {!info.closed && <p className="text-[11px] text-slate-400 text-center mt-6">
          Os seus documentos são tratados de forma confidencial no âmbito {deal(info.negocio).ctx}.
        </p>}
        {info.privacy && <p className="text-center mt-2"><PrivacyNotice p={info.privacy} /></p>}
        {info.powered !== false && (
          <p className="text-center mt-2"><PoweredBy testid="portal-powered-by" /></p>
        )}
      </div>
    </div>
    </>
  );
}
